import React, { useState } from 'react';
import { 
  Sparkles, Check, ArrowRight, ShieldCheck, 
  Calendar, PhoneCall, CheckCircle2, 
  Eye, Award
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { salonInfo } from '../data/salonInfo';

// High-fidelity Hair Loss Types and Stages Data with full NL, EN, TR translations
const HAIR_LOSS_DATA = [
  {
    id: "type-1",
    tabLabel: { nl: "Type 1", en: "Type 1", tr: "Tip 1" },
    title: {
      nl: "Type 1: Inhammen & Voorste Haarlijn",
      en: "Type 1: Receding Hairline & Temples",
      tr: "Tip 1: Alın & Şakak Açılması"
    },
    subTitle: {
      nl: "Haarverlies beginnend bij de slapen en terugtrekkende voorhoofdslijn.",
      en: "Hair loss starting at the temples with receding frontal hairline.",
      tr: "Şakaklardan başlayan ve alın çizgisini geriye çeken dökülme."
    },
    stages: [
      {
        id: "t1-s1",
        image: "/images/t1_s1.png",
        name: { nl: "Normaal", en: "Normal", tr: "Normal" },
        desc: { nl: "Geen haarverlies / Gezonde haardos", en: "No hair loss / Healthy hair", tr: "Saç dökülmesi yok / Sağlıklı saç" },
        recommended: {
          title: { nl: "Geen Haarwerk Nodig", en: "No Hair System Needed", tr: "Protez Saç Gerekmez" },
          system: { nl: "Signature Knipbeurt & Verzorging", en: "Signature Haircut & Scalp Care", tr: "Özel Saç Kesimi & Bakım" },
          text: {
            nl: "Uw haardos is in uitstekende conditie. Wij adviseren onze signature knipbeurt en hoofdhuidverzorging.",
            en: "Your hair is in great condition. Regular care and styling keeps hair strong and dense.",
            tr: "Saçlarınız gayet sağlıklı. Koruyucu bakım ve düzenli kesim önerilir."
          }
        }
      },
      {
        id: "t1-s2",
        image: "/images/t1_s2.png",
        name: { nl: "Beginfase", en: "Early Stage", tr: "Başlangıç" },
        desc: { nl: "Lichte inhammen bij de slapen", en: "Mild temple recession", tr: "Şakaklarda hafif açılma" },
        recommended: {
          title: { nl: "Frontal Hairpiece / V-Loop PU", en: "Frontal V-Loop System", tr: "Ön Bölge / Şakak Protezi" },
          system: { nl: "Mini Frontal 100% Echt Haar", en: "Mini Frontal 100% Human Hair", tr: "%100 Gerçek İnsan Saçı Ön Protez" },
          text: {
            nl: "Ideaal voor het herstellen van een strakke, jeugdige voorste haarlijn. Wordt naadloos aangesloten op uw eigen haar.",
            en: "Perfect for restoring a sharp, youthful front hairline without covering the entire head.",
            tr: "Sadece alın ve şakak hattını dolduran, kendi saçınızla sıfır geçişli mikro protez."
          }
        }
      },
      {
        id: "t1-s3",
        image: "/images/t1_s3.png",
        name: { nl: "Fase 2", en: "Stage 2", tr: "2. Aşama" },
        desc: { nl: "Duidelijke M-vormige inhammen", en: "Deep temple recession (M-shape)", tr: "Derinleşen M-tipi şakak açılması" },
        recommended: {
          title: { nl: "Semi-Maatwerk Frontal & Top", en: "Frontal & Mid-Scalp System", tr: "Ön ve Orta Bölge Protezi" },
          system: { nl: "Ultra-Thin Skin PU (0.03mm)", en: "Ultra-Thin Skin PU (0.03mm)", tr: "Ultra İnce 0.03mm Şeffaf Mikro Deri" },
          text: {
            nl: "Vult de volledige voorste zone op met een niet van echt te onderscheiden haarlijn. U kunt het haar weer vrij naar achteren stylen.",
            en: "Fills the entire front zone with an invisible hairline. Freely style slick-backs, pompadours or crops.",
            tr: "Ön saç çizgisini tamamen yeniler. Saçınızı geriye veya yana rahatça tarayabilirsiniz."
          }
        }
      },
      {
        id: "t1-s4",
        image: "/images/t1_s4.png",
        name: { nl: "Vergevorderd", en: "Advanced", tr: "Son Aşama" },
        desc: { nl: "Vergevorderd frontaal haarverlies", en: "Advanced frontal loss", tr: "İleri düzey ön bölge dökülmesi" },
        recommended: {
          title: { nl: "Volledig Topstuk / Full Frontal", en: "Full Top Hair System", tr: "Tam Üst Bölge Protez Saç" },
          system: { nl: "100% Human Hair Semi-Maatwerk", en: "100% Human Hair Custom Blend", tr: "%100 Gerçek İnsan Saçı Özel Sistem" },
          text: {
            nl: "Biedt 100% dekking voor de gehele voor- en bovenzijde. Onmiddellijk een volle haardos met een natuurlijke overloop naar de zijkanten.",
            en: "Provides complete coverage for the entire top area, seamlessly blended with sides and back.",
            tr: "Tüm üst bölgeyi kaplayan, yanlarla mükemmel harmanlanan dolgun saç sistemi."
          }
        }
      }
    ]
  },
  {
    id: "type-2",
    tabLabel: { nl: "Type 2", en: "Type 2", tr: "Tip 2" },
    title: {
      nl: "Type 2: Kruin & Bovenkant (Vertex)",
      en: "Type 2: Crown & Vertex Thinning",
      tr: "Tip 2: Tepe & Kruin Dökülmesi"
    },
    subTitle: {
      nl: "Haaruitval gecentreerd op de kruin met behoud van de voorste haarlijn.",
      en: "Hair loss centered on the crown with intact or mild front hairline.",
      tr: "Ön hat korunurken tepe bölgesinde oluşan dairesel açılma."
    },
    stages: [
      {
        id: "t2-s1",
        image: "/images/t2_s1.png",
        name: { nl: "Normaal", en: "Normal", tr: "Normal" },
        desc: { nl: "Volle kruin, geen haarverlies", en: "Full crown, no hair loss", tr: "Dolgun tepe, dökülme yok" },
        recommended: {
          title: { nl: "Geen Haarwerk Nodig", en: "No System Needed", tr: "Protez Saç Gerekmez" },
          system: { nl: "Verzorgende Keratine Kuur", en: "Keratin & Scalp Care", tr: "Keratin & Saç Bakımı" },
          text: {
            nl: "Uw kruin is volledig bedekt. Wij adviseren verzorgende behandelingen ter stimulatie van de haarwortels.",
            en: "Your crown has great density. Regular care keeps hair follicles healthy.",
            tr: "Tepe bölgeniz dolgun. Koruyucu bakım ve besleyici ampul önerilir."
          }
        }
      },
      {
        id: "t2-s2",
        image: "/images/t2_s2.png",
        name: { nl: "Beginfase", en: "Early Stage", tr: "Başlangıç" },
        desc: { nl: "Lichte uitdunning op de kruin", en: "Thinning crown spot", tr: "Tepe bölgesinde hafif seyrelme" },
        recommended: {
          title: { nl: "Crown Topstuk (Kruin Systeem)", en: "Crown Micro-Piece", tr: "Tepe Bölgesi Mikro Protez" },
          system: { nl: "Ademende Lace & PU Rand", en: "Breathable Lace & PU Ring", tr: "Hava Geçirgen Tül & PU Çerçeve" },
          text: {
            nl: "Een compact haarstukje dat exact over de dunne kruinzone valt. 100% onzichtbaar van achteren en bovenaf gezien.",
            en: "A discreet micro-system covering the thinning vertex. Completely undetectable from all angles.",
            tr: "Sadece tepe bölgesine yerleştirilen, arkadan bakıldığında sıfır geçiş sağlayan mikro sistem."
          }
        }
      },
      {
        id: "t2-s3",
        image: "/images/t2_s3.png",
        name: { nl: "Fase 2", en: "Stage 2", tr: "2. Aşama" },
        desc: { nl: "Zichtbare kale plek op de kruin", en: "Visible crown bald spot", tr: "Belirginleşen tepe kelliği" },
        recommended: {
          title: { nl: "Crown & Vertex Hair System", en: "Crown & Vertex System", tr: "Geniş Tepe Protezi" },
          system: { nl: "100% Human Hair PU Basis", en: "100% Human Hair PU Base", tr: "%100 Gerçek Saç Şeffaf PU" },
          text: {
            nl: "Dekt de gehele kruinzone af en herstelt de natuurlijke haarinplant en wervel. Blijft 4-6 weken muurvast zitten.",
            en: "Covers the full crown area, reconstructing the natural hair swirl. Stays securely bonded for 4-6 weeks.",
            tr: "Tepe dönerini ve doğal yönünü yeniden inşa eder. Suya, tere ve spora tam dayanıklıdır."
          }
        }
      },
      {
        id: "t2-s4",
        image: "/images/t2_s4.png",
        name: { nl: "Vergevorderd", en: "Advanced", tr: "Son Aşama" },
        desc: { nl: "Grote kale zone op de kruin", en: "Large bald crown area", tr: "Geniş alana yayılan tepe dökülmesi" },
        recommended: {
          title: { nl: "Full Top & Crown System", en: "Full Crown System", tr: "Genişletilmiş Tepe Protezi" },
          system: { nl: "Semi-Maatwerk 100% Echt Haar", en: "Semi-Custom 100% Real Hair", tr: "Yarı Özel %100 Doğal Saç" },
          text: {
            nl: "Voor wie een grote kruinzone wil herstellen. Wordt perfect gemengd met de zijkanten voor een volle look.",
            en: "Ideal for wider crown baldness. Custom cut and blended into your side and back hair.",
            tr: "Büyük tepe açılmalarını sıfırdan kapatan, kendi yan saçlarınızla bütünleşen dolgun saç."
          }
        }
      }
    ]
  },
  {
    id: "type-3",
    tabLabel: { nl: "Type 3", en: "Type 3", tr: "Tip 3" },
    title: {
      nl: "Type 3: Gecombineerd (Inhammen + Kruin)",
      en: "Type 3: Combined (Hairline + Crown)",
      tr: "Tip 3: Birleşik (Alın + Tepe Açılması)"
    },
    subTitle: {
      nl: "Haarverlies op zowel de voorste haarlijn als op de kruin.",
      en: "Simultaneous hair loss at both the frontal hairline and the vertex crown.",
      tr: "Hem ön çizgide hem de tepe bölgesinde eşzamanlı saç dökülmesi."
    },
    stages: [
      {
        id: "t3-s1",
        image: "/images/t3_s1.png",
        name: { nl: "Normaal", en: "Normal", tr: "Normal" },
        desc: { nl: "Geen haaruitval", en: "No hair loss", tr: "Saç dökülmesi yok" },
        recommended: {
          title: { nl: "Geen Haarwerk Nodig", en: "No Hair System Needed", tr: "Protez Gerekmez" },
          system: { nl: "Preventieve Verzorging", en: "Preventative Grooming", tr: "Önleyici Bakım" },
          text: {
            nl: "Geen behandeling vereist. Geniet van onze signature knipbeurten!",
            en: "No hairpiece required. Enjoy our signature haircut & styling services!",
            tr: "Protez gerekmemektedir. Klasik saç ve sakal hizmetlerimizden faydalanabilirsiniz."
          }
        }
      },
      {
        id: "t3-s2",
        image: "/images/t3_s2.png",
        name: { nl: "Beginfase", en: "Early Stage", tr: "Başlangıç" },
        desc: { nl: "Inhammen en lichte kruinuitdunning", en: "Temples & mild crown thinning", tr: "Alın ve tepede hafif açılma" },
        recommended: {
          title: { nl: "Dual-Zone Hair System", en: "Dual-Zone Hairpiece", tr: "İkili Bölge Protez Sistemi" },
          system: { nl: "Ultra-Thin Skin 0.03mm", en: "Ultra-Thin Skin 0.03mm", tr: "Ultra İnce Mikro Deri" },
          text: {
            nl: "Herstelt zowel de voorste lijn als de kruin in één naadloos haarwerk. Niet voelbaar en 100% ademend.",
            en: "Restores both the front and crown in one seamless, feather-light hair system.",
            tr: "Ön ve tepeyi tek parçada kusursuz birleştiren, kafada ağırlık yapmayan sistem."
          }
        }
      },
      {
        id: "t3-s3",
        image: "/images/t3_s3.png",
        name: { nl: "Fase 2", en: "Stage 2", tr: "2. Aşama" },
        desc: { nl: "Diepe inhammen en kale kruin", en: "Deep temples & crown baldness", tr: "Derin inhammen ve açık tepe" },
        recommended: {
          title: { nl: "The Ultimate Top System", en: "The Ultimate Top System", tr: "Komple Üst Protez Saç" },
          system: { nl: "100% Echt Haar + Fade Blending", en: "100% Real Hair + Fade Blend", tr: "%100 Gerçek Saç + Fade Geçiş" },
          text: {
            nl: "Onze populairste transformatie! Geeft u binnen 90 minuten een volle kop met haar, klaar voor elke gewenste stijl.",
            en: "Our most popular transformation! Walk out with a full head of hair ready for any modern fade cut.",
            tr: "En popüler dönüşümümüz! 90 dakikada 10 yaş gençleştiren dolgun saç ve kusursuz fade kesim."
          }
        }
      },
      {
        id: "t3-s4",
        image: "/images/t3_s4.png",
        name: { nl: "Vergevorderd", en: "Advanced", tr: "Son Aşama" },
        desc: { nl: "Volledig kale bovenkant", en: "Full top baldness", tr: "Tamamen açık üst bölge" },
        recommended: {
          title: { nl: "Full Coverage Hairpiece (Semi-Maatwerk)", en: "Full Coverage Hair System", tr: "Tam Kapsamlı Protez Saç" },
          system: { nl: "PU/Lace Hybride 100% Human Hair", en: "PU/Lace Hybrid 100% Human Hair", tr: "PU / Tül Hibrit %100 Gerçek Saç" },
          text: {
            nl: "Volledige transformatie van de gehele bovenzijde. Zwemmen, sporten, douchen en slapen alsof het uw eigen haar is.",
            en: "Total top restoration. Swim, shower, run, and live life with zero restrictions.",
            tr: "Tüm üst bölgeyi sıfırdan doldurur. Kendi saçınız gibi yıkayabilir, tarayabilir ve denize girebilirsiniz."
          }
        }
      }
    ]
  },
  {
    id: "type-4",
    tabLabel: { nl: "Type 4", en: "Type 4", tr: "Tip 4" },
    title: {
      nl: "Type 4: Vergevorderde Kaalheid (Norwood 5-7)",
      en: "Type 4: Advanced Hair Loss (Norwood 5-7)",
      tr: "Tip 4: İleri Düzey Kellik (Norwood 5-7)"
    },
    subTitle: {
      nl: "Alleen nog haar aan de zijkanten en achterkant (hoefijzerpatroon).",
      en: "Hair remaining primarily on the sides and back in a horseshoe pattern.",
      tr: "Yalnızca yanlarda ve ensede saç kalmış ileri düzey kellik."
    },
    stages: [
      {
        id: "t4-s1",
        image: "/images/t4_s1.png",
        name: { nl: "Normaal", en: "Normal", tr: "Normal" },
        desc: { nl: "Geen haarverlies", en: "No hair loss", tr: "Saç dökülmesi yok" },
        recommended: {
          title: { nl: "Geen Haarwerk Nodig", en: "No System Needed", tr: "Protez Gerekmez" },
          system: { nl: "Signature Kappersservice", en: "Signature Barber Cut", tr: "Klasik Kesim" },
          text: {
            nl: "Geen haarwerk nodig. Kom langs voor een strakke knipbeurt bij Aura Hair!",
            en: "No hairpiece required. Visit us for precision cuts and fades!",
            tr: "Protez saç gerekmez. Salonumuza klasik saç kesimi için bekleriz!"
          }
        }
      },
      {
        id: "t4-s2",
        image: "/images/t4_s2.png",
        name: { nl: "Fase 5", en: "Stage 5", tr: "Aşama 5 (Norwood 5)" },
        desc: { nl: "Smal bruggetje bovenop", en: "Thin bridge remaining on top", tr: "Tepede kalan ince saç köprüsü" },
        recommended: {
          title: { nl: "Complete Top Restoration System", en: "Full Top Restoration", tr: "Komple Üst Restorasyon Protezi" },
          system: { nl: "100% Echt Menselijk Haar", en: "100% Human Hair System", tr: "%100 Gerçek İnsan Saçı" },
          text: {
            nl: "Vervangt de uitgedunde bovenkant en sluit vlekkeloos aan op uw zij- en achterhaar voor een natuurlijk volume.",
            en: "Completely eliminates the bald area with high density 100% human hair.",
            tr: "Seyrelmiş bölgeyi tamamen kapatıp yan saçlarla kusursuz birleşir."
          }
        }
      },
      {
        id: "t4-s3",
        image: "/images/t4_s3.png",
        name: { nl: "Fase 6", en: "Stage 6", tr: "Aşama 6 (Norwood 6)" },
        desc: { nl: "Brug verdwenen, brede opening", en: "Bridge gone, wide opening", tr: "Köprü kaybolmuş, geniş açılma" },
        recommended: {
          title: { nl: "Full Crown & Frontal Hybrid", en: "Full Hybrid Hair System", tr: "Tam Hibrit Protez Saç" },
          system: { nl: "Ademende Polyurethaan Micro-Skin", en: "Breathable PU Micro-Skin", tr: "Nefes Alan Mikro PU Deri" },
          text: {
            nl: "De ultieme oplossing voor wie al jaren kaal is. Geeft direct een jonge, frisse uitstraling met volle haardichtheid.",
            en: "The ultimate solution for long-term baldness. Instant 10-year age reversal with full volume.",
            tr: "Yıllardır saçsız olanlar için devrim niteliğinde çözüm. Anında genç ve dinamik görünüm."
          }
        }
      },
      {
        id: "t4-s4",
        image: "/images/t4_s4.png",
        name: { nl: "Fase 7", en: "Stage 7", tr: "Son Aşama (Norwood 7)" },
        desc: { nl: "Alleen haar aan zijkanten/achterkant", en: "Full horseshoe sides only", tr: "Yalnızca yan ve ensede saç kalmış" },
        recommended: {
          title: { nl: "Grand Master Full Hairpiece", en: "Grand Master Hair System", tr: "Grand Master Full Protez Saç" },
          system: { nl: "Semi-Maatwerk + Skin Fade Cut", en: "Semi-Custom + Skin Fade Cut", tr: "Özel Tasarım + Skin Fade Kesim" },
          text: {
            nl: "Zelfs bij maximale kaalheid creëren wij een volle, natuurlijk bewegende haardos die perfect past bij uw gezicht.",
            en: "Even with maximum baldness, we recreate a natural, flowing hairstyle tailored to your face structure.",
            tr: "En ileri düzey kellik durumunda bile yüz yapınıza özel %100 doğal görünümlü saç tasarımı."
          }
        }
      }
    ]
  }
];

export default function HairpieceSection({ onBookIntake }) {
  const { lang, t } = useLanguage();
  const [selectedTypeIdx, setSelectedTypeIdx] = useState(0);
  const [selectedStageIdx, setSelectedStageIdx] = useState(1);

  const currentType = HAIR_LOSS_DATA[selectedTypeIdx];
  const currentStage = currentType.stages[selectedStageIdx] || currentType.stages[0];

  const typeTitle = typeof currentType.title === 'object' ? (currentType.title[lang] || currentType.title.nl) : currentType.title;
  const stageName = typeof currentStage.name === 'object' ? (currentStage.name[lang] || currentStage.name.nl) : currentStage.name;
  const stageDesc = typeof currentStage.desc === 'object' ? (currentStage.desc[lang] || currentStage.desc.nl) : currentStage.desc;
  
  const rec = currentStage.recommended;
  const recTitle = typeof rec.title === 'object' ? (rec.title[lang] || rec.title.nl) : rec.title;
  const recSystem = typeof rec.system === 'object' ? (rec.system[lang] || rec.system.nl) : rec.system;
  const recText = typeof rec.text === 'object' ? (rec.text[lang] || rec.text.nl) : rec.text;

  const hpT = t.hairpiece;

  return (
    <section id="haarwerk" className="py-24 relative bg-slate-50 border-t border-slate-200 overflow-hidden">
      
      {/* Subtle Warm Amber / Emerald Glow */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-amber-200/30 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-10 right-10 w-96 h-96 bg-emerald-200/30 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Section Header */}
        <div className="text-center max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-100 border border-emerald-300 text-emerald-800 text-xs font-bold uppercase tracking-wider mb-4 shadow-sm">
            <Sparkles className="w-3.5 h-3.5 text-emerald-600" />
            <span>{hpT.badge}</span>
          </div>

          <h2 className="font-serif text-3xl sm:text-5xl font-extrabold text-slate-900 tracking-tight leading-[1.2]">
            {hpT.mainTitle} <br />
            <span className="gold-gradient-text">{hpT.subGoldTitle}</span>
          </h2>

          <p className="mt-4 text-sm sm:text-base text-slate-600 font-normal leading-relaxed max-w-3xl mx-auto">
            {hpT.description}
          </p>
        </div>

        {/* 📋 STEP 1: EXACT STAGE SELECTOR */}
        <div className="mt-14 p-6 sm:p-10 rounded-3xl bg-white border border-slate-200 shadow-xl">
          
          <div className="text-center mb-8">
            <span className="inline-block px-4 py-1.5 rounded-lg bg-[#7a9c59] text-white font-mono font-bold text-xs uppercase tracking-wider mb-2 shadow-sm">
              {hpT.step1Badge}
            </span>
            <h3 className="font-serif text-xl sm:text-2xl font-bold text-slate-900 mt-1">
              {typeTitle}
            </h3>
            <p className="text-xs text-slate-500 mt-1">
              {typeof currentType.subTitle === 'object' ? (currentType.subTitle[lang] || currentType.subTitle.nl) : currentType.subTitle}
            </p>
          </div>

          {/* Type Selector Tabs */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5 max-w-3xl mx-auto mb-8 sm:mb-10">
            {HAIR_LOSS_DATA.map((tItem, tIdx) => {
              const isSelected = selectedTypeIdx === tIdx;
              return (
                <button
                  key={tItem.id}
                  onClick={() => {
                    setSelectedTypeIdx(tIdx);
                    setSelectedStageIdx(1);
                  }}
                  className={`py-2.5 px-3 rounded-xl border text-xs font-bold transition-all flex items-center justify-center gap-2 ${
                    isSelected
                      ? 'bg-[#7a9c59] text-white border-[#7a9c59] shadow-md shadow-[#7a9c59]/30 font-bold'
                      : 'bg-slate-50 border-slate-200 text-slate-700 hover:text-slate-900 hover:bg-slate-100'
                  }`}
                >
                  <span className="text-xs sm:text-sm font-semibold truncate">{tItem.tabLabel[lang] || tItem.tabLabel.nl}</span>
                </button>
              );
            })}
          </div>

          {/* 4 Stage Cards Grid with Clean Head Illustrations */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6">
            {currentType.stages.map((stageItem, sIdx) => {
              const isStageSelected = selectedStageIdx === sIdx;
              const sName = typeof stageItem.name === 'object' ? (stageItem.name[lang] || stageItem.name.nl) : stageItem.name;
              const sDesc = typeof stageItem.desc === 'object' ? (stageItem.desc[lang] || stageItem.desc.nl) : stageItem.desc;

              return (
                <div
                  key={stageItem.id}
                  onClick={() => setSelectedStageIdx(sIdx)}
                  className={`p-3 sm:p-5 rounded-2xl cursor-pointer transition-all duration-300 flex flex-col justify-between group ${
                    isStageSelected
                      ? 'bg-amber-50/70 border-2 border-amber-500 shadow-xl scale-[1.02] ring-2 ring-amber-400/20'
                      : 'bg-slate-50/80 border border-slate-200 hover:border-amber-400 hover:bg-white'
                  }`}
                >
                  {/* Clean White Card Background for Head Photo */}
                  <div className="w-full h-32 sm:h-44 bg-white rounded-xl flex items-center justify-center p-2 sm:p-3 shadow-sm overflow-hidden border border-slate-200 group-hover:scale-[1.02] transition-transform">
                    <img
                      src={stageItem.image}
                      alt={sName}
                      className="max-h-full max-w-full object-contain"
                    />
                  </div>

                  {/* Stage Title and Description */}
                  <div className="mt-3 sm:mt-4 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <h4 className="font-serif text-sm sm:text-lg font-bold text-slate-900 group-hover:text-amber-700 transition-colors">
                        {sName}
                      </h4>
                      {isStageSelected && (
                        <CheckCircle2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-amber-600 shrink-0" />
                      )}
                    </div>
                    <p className="text-[11px] sm:text-xs text-slate-600 mt-1 leading-snug font-medium line-clamp-2">
                      {sDesc}
                    </p>
                  </div>

                  {/* Select Status Indicator */}
                  <div className="mt-3 sm:mt-4 pt-2 sm:pt-3 border-t border-slate-200 text-center">
                    <span className={`text-[10px] sm:text-[11px] font-bold uppercase tracking-wider ${
                      isStageSelected ? 'text-amber-700 font-extrabold' : 'text-slate-400 group-hover:text-slate-600'
                    }`}>
                      {isStageSelected ? hpT.selectedCard : hpT.selectCard}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* 🎯 STEP 2: TAILORED SOLUTION & INSTANT ACTION */}
          <div className="mt-10 p-6 sm:p-8 rounded-2xl bg-gradient-to-r from-amber-50 via-white to-amber-50 border border-amber-300 shadow-md flex flex-col lg:flex-row items-center justify-between gap-6">
            
            <div className="flex-1 space-y-2 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold border border-emerald-300 shadow-sm">
                <Check className="w-3.5 h-3.5 text-emerald-600" />
                <span>
                  {`${hpT.recBadge} ${typeTitle} (${stageName})`}
                </span>
              </div>

              <h4 className="font-serif text-xl sm:text-2xl font-bold text-slate-900">
                {recTitle}
              </h4>

              <div className="flex items-center justify-center lg:justify-start gap-3 text-xs text-amber-800 font-semibold">
                <span>{recSystem}</span>
                <span>&bull;</span>
                <span>{hpT.humanHairFeature}</span>
                <span>&bull;</span>
                <span>{hpT.puBaseFeature}</span>
              </div>

              <p className="text-xs sm:text-sm text-slate-600 leading-relaxed font-normal max-w-2xl">
                {recText}
              </p>
            </div>

            {/* Action CTAs */}
            <div className="flex flex-col sm:flex-row lg:flex-col gap-3 shrink-0 w-full sm:w-auto">
              <button
                onClick={onBookIntake}
                className="px-7 py-3.5 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-white font-bold text-xs tracking-wider uppercase shadow-lg shadow-amber-500/25 hover:scale-105 active:scale-95 transition-all flex items-center justify-center gap-2"
              >
                <Calendar className="w-4 h-4" />
                <span>{hpT.bookFreeIntakeBtn}</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </button>

              <a
                href={`tel:${salonInfo.phoneRaw}`}
                className="px-6 py-3 rounded-xl bg-amber-50 hover:bg-amber-100 border border-amber-300 text-amber-900 font-bold text-xs transition-all flex items-center justify-center gap-2 shadow-sm"
              >
                <PhoneCall className="w-4 h-4 text-amber-600" />
                <span>{lang === 'nl' ? 'Bel voor Advies' : lang === 'tr' ? 'Bizi Doğrudan Arayın' : 'Call for Advice'}</span>
              </a>
            </div>

          </div>

        </div>



        {/* 🌟 4 CORE GUARANTEES */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="p-6 rounded-2xl bg-white border border-slate-200 shadow-sm hover:border-amber-300 transition-all">
            <div className="w-10 h-10 rounded-xl bg-amber-100 border border-amber-200 flex items-center justify-center text-amber-700 mb-3">
              <Check className="w-5 h-5" />
            </div>
            <h4 className="font-serif text-base font-bold text-slate-900">
              {hpT.guarantee1Title}
            </h4>
            <p className="text-xs text-slate-600 mt-1.5 leading-relaxed">
              {hpT.guarantee1Desc}
            </p>
          </div>

          <div className="p-6 rounded-2xl bg-white border border-slate-200 shadow-sm hover:border-amber-300 transition-all">
            <div className="w-10 h-10 rounded-xl bg-amber-100 border border-amber-200 flex items-center justify-center text-amber-700 mb-3">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <h4 className="font-serif text-base font-bold text-slate-900">
              {hpT.guarantee2Title}
            </h4>
            <p className="text-xs text-slate-600 mt-1.5 leading-relaxed">
              {hpT.guarantee2Desc}
            </p>
          </div>

          <div className="p-6 rounded-2xl bg-white border border-slate-200 shadow-sm hover:border-amber-300 transition-all">
            <div className="w-10 h-10 rounded-xl bg-amber-100 border border-amber-200 flex items-center justify-center text-amber-700 mb-3">
              <Sparkles className="w-5 h-5" />
            </div>
            <h4 className="font-serif text-base font-bold text-slate-900">
              {hpT.guarantee3Title}
            </h4>
            <p className="text-xs text-slate-600 mt-1.5 leading-relaxed">
              {hpT.guarantee3Desc}
            </p>
          </div>

          <div className="p-6 rounded-2xl bg-white border border-slate-200 shadow-sm hover:border-amber-300 transition-all">
            <div className="w-10 h-10 rounded-xl bg-amber-100 border border-amber-200 flex items-center justify-center text-amber-700 mb-3">
              <Eye className="w-5 h-5" />
            </div>
            <h4 className="font-serif text-base font-bold text-slate-900">
              {hpT.guarantee4Title}
            </h4>
            <p className="text-xs text-slate-600 mt-1.5 leading-relaxed">
              {hpT.guarantee4Desc}
            </p>
          </div>
        </div>

      </div>
    </section>
  );
}
